import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';

import { submit, isSubmitting, isDirty, getFormError, getFormValues } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import Button from 'components/common/Button';

import PatientInformationEdit from './PatientInformationEdit';
import PatientInformationEditConfirmationDialog from './PatientInformationEditConfirmationDialog';

const styles = theme => ({
  button: {
    marginLeft: 20,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 8,
    },
  },
  subheadingDetails: {
    marginLeft: 15,
    color: theme.palette.grey[600],
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  root: {
    position: 'relative',
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      transition: theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.short,
      }),
      top: -9,
      right: -9,
      bottom: -9,
      left: -9,
      borderRadius: 10,
      border: '2px solid transparent',
      pointerEvents: 'none',
    },
  },
  rootDirty: {
    '&:after': {
      borderColor: theme.palette.primary[500],
    },
  },
  rootVariantCreate: {
    marginLeft: 74,
    marginRight: 74,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  bodyVariantCreate: {
    paddingLeft: 130,
    paddingRight: 130,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
});

class PatientInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      confirmDialogOpen: false,
      forceUpdate: false,
    };

    this.onSaveButtonClick = this.onSaveButtonClick.bind(this);
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
    this.onConfirmDialogClose = this.onConfirmDialogClose.bind(this);
    this.onConfirmDialogSubmit = this.onConfirmDialogSubmit.bind(this);
  }

  onSaveButtonClick() {
    const { dirty, actions, variant, onCancel, error } = this.props;
    if (error && error.meta && error.meta.patients) {
      this.setState({ confirmDialogOpen: true });
    } else {
      if (dirty || variant === 'create') {
        actions.submit('patientInformation');
      } else {
        onCancel();
      }
    }
  }

  onSubmitSuccess(values) {
    const { variant, patient, onCreate, onCancel } = this.props;
    if (variant === 'create') {
      if (!patient) {
        onCreate(values['Record Number']);
      }
    } else {
      onCancel();
    }
  }

  onConfirmDialogClose() {
    this.setState({ confirmDialogOpen: false });
  }

  onConfirmDialogSubmit() { 
    const { actions } = this.props;
    this.setState({
      forceUpdate: true,
      confirmDialogOpen: false
    });
    setTimeout(() => {
      actions.submit('patientInformation');
    }, 200);
  }

  render() {
    const {
      classes,
      patient,
      submitting,
      variant,
      onCancel,
      formValues,
      dirty,
      error,
    } = this.props;
    const { confirmDialogOpen, forceUpdate } = this.state;
    let initialValues;
    if (patient !== null) {
      initialValues = {
        'First Name': patient['First Name'],
        'Middle Name': patient['Middle Name'] || '',
        'Last Name': patient['Last Name'],
        'Date of Birth': patient['Date of Birth'],
        'Date of Death': patient['Date of Death'],
        'Social Security Number': patient['Social Security Number'],
        Gender: patient.Gender,
        'Patient Status': patient['Patient Status'].toString(),
      };
    }
    const forceCreateText = variant === 'create' ? 'Create Anyway' : 'Save Anyway';
    return (
      <Section
        className={classNames(classes.root, {
          [classes.rootDirty]: dirty,
          [classes.rootVariantCreate]: variant === 'create',
        })}
      >
        <SectionHeader>
          <SectionTitle>
            Patient Information
            {patient &&
              <span className={classes.subheadingDetails}>
                Record Number: {patient['Record Number']}
              </span>
            }
          </SectionTitle>
          <div>
            <Button
              className={classes.button}
              variant="raised"
              onClick={() => {
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button
              className={classes.button}
              variant="raised"
              color="primary"
              disabled={submitting}
              onClick={this.onSaveButtonClick}
            >
              {error && error.meta && error.meta.patients ? forceCreateText : 'Save'}
            </Button>
          </div>
        </SectionHeader>
        <SectionBody
          className={classNames({
            [classes.bodyVariantCreate]: variant === 'create',
          })}
        >
          <PatientInformationEdit
            form="patientInformation"
            forceUpdate={forceUpdate}
            {... initialValues ? { initialValues } : {}}
            {... patient ? { 'Record Number': patient['Record Number'] } : {}}
            onSubmitSuccess={this.onSubmitSuccess}
          />
        </SectionBody>
        <PatientInformationEditConfirmationDialog
          variant={variant}
          open={confirmDialogOpen}
          onClose={this.onConfirmDialogClose}
          onSubmit={this.onConfirmDialogSubmit}
          {...{
            'First Name': formValues ? formValues['First Name'] : '',
            'Last Name': formValues ? formValues['Last Name'] : '',
            'Middle Name': formValues ? formValues['Middle Name'] : '',
          }}
        />
      </Section>
    );
  }
}

PatientInformation.propTypes = {
  patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
  }),
  formValues: PropTypes.shape({
    'First Name': PropTypes.string,
  }),
  submitting: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(['create', 'edit']),
  dirty: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    submit: PropTypes.func.isRequired,
  }).isRequired,
  onCreate: PropTypes.func,
  onCancel: PropTypes.func,
  error: PropTypes.oneOfType([
    PropTypes.shape({
      error: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  classes: PropTypes.shape({
    rootDirty: PropTypes.string.isRequired,
    rootVariantCreate: PropTypes.string.isRequired,
    bodyVariantCreate: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    subheadingDetails: PropTypes.string.isRequired,
  }).isRequired,
};

PatientInformation.defaultProps = {
  patient: null,
  error: null,
  formValues: null,
  variant: 'edit',
  onCreate: () => {},
  onCancel: () => {},
};

const mapStateToProps = store => ({
  submitting: isSubmitting('patientInformation')(store),
  dirty: isDirty('patientInformation')(store),
  error: getFormError('patientInformation')(store),
  formValues: getFormValues('patientInformation')(store),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ submit }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(PatientInformation),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/PatientInformation/PatientInformation.js